// STRUCT CODE Design Tokens v2.0 — "Night Horoscope"
// 2026-05-15 / 詳細仕様: docs/UI_REDESIGN_PLAN_20260515.md
//
// 既存の styled-components が theme.colors.{primary,bgPrimary,text,...} を
// 参照しているため、既存キー名を保ちつつ新しい値（夜空テーマ）に差し替える。
// 新コンポーネント (Pentagon / StarChart 等) は theme.colors.starGold など
// 新キーを使う。

const colors = {
  // === Surfaces (night sky) ===
  bgPrimary:    '#0A0D1A',  // 最深夜 — ページ最背面
  bgSecondary:  '#131A2E',  // 寝室の藍 — セクション
  bgTertiary:   '#1A2240',  // 藩色 — カード
  bgHover:      '#232C50',  // hover / elevated
  bgTint:       'rgba(255,255,255,0.03)',

  // legacy aliases (既存コンポーネント互換)
  background:   '#131A2E',
  surface:      '#1A2240',

  // === Inks (paper on night) ===
  text:           '#F1ECDF',  // 古紙色 — 本文
  textSecondary:  '#C4C8D8',  // やわらかい灰白
  textMuted:      '#8A93B0',  // 銘銘のグレー
  textLight:      '#4D5470',  // 線・キャプション
  textFaint:      '#4D5470',

  // === Accents ===
  // 主役: 黄金（CTA・タイプコード・ヘアライン）
  primary:        '#E6C77E',
  primaryGlow:    'rgba(230,199,126,0.18)',
  secondary:      '#9C7BC9',  // 紫水晶
  accent:         '#D08A8A',  // 金星色

  // === 5-Axis fixed colors (Pentagon / Spectrum chart 共通) ===
  starAmber:   '#F4B860',  // 起動 Activation — 太陽
  starBlue:    '#6B8CCB',  // 判断 Judgment — 水星
  starViolet:  '#9C7BC9',  // 選択 Choice — 木星
  starRose:    '#D08A8A',  // 共鳴 Resonance — 金星
  starMint:    '#7FB89A',  // 自覚 Awareness — 月
  starGold:    '#E6C77E',  // 主役アクセント — 結果・タイトル装飾

  // === Lines ===
  border:       'rgba(241,236,223,0.08)',
  borderLight:  'rgba(241,236,223,0.14)',
  borderHover:  'rgba(241,236,223,0.28)',
  lineFaint:    'rgba(241,236,223,0.08)',
  lineSoft:     'rgba(241,236,223,0.14)',
  lineStrong:   'rgba(241,236,223,0.28)',
  lineGold:     'rgba(230,199,126,0.30)',

  // === Status (controlled, sober) ===
  success: '#7FB89A',
  warning: '#E6C77E',
  error:   '#D08A8A',

  // === Gradients ===
  gradient:     'linear-gradient(135deg, #1A2240 0%, #28204F 50%, #3D2A55 100%)',
  gradientSoft: 'linear-gradient(135deg, rgba(107,140,203,0.10) 0%, rgba(156,123,201,0.10) 50%, rgba(208,138,138,0.10) 100%)',
  gradAurora:   'linear-gradient(135deg, #6B8CCB 0%, #9C7BC9 50%, #D08A8A 100%)',
  gradGold:     'linear-gradient(135deg, #E6C77E 0%, #F4B860 100%)',
  gradCard:     'linear-gradient(180deg, #1A2240 0%, #131A2E 100%)',
  gradSky:      'linear-gradient(135deg, #1A2240 0%, #28204F 50%, #3D2A55 100%)',
};

const fonts = {
  // Display: 占星術書の品位
  display: "'Cormorant Garamond', 'Shippori Mincho B1', 'Noto Serif JP', serif",
  // Body: 読みやすさ
  main:    "'Inter', 'Noto Sans JP', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: "'Cormorant Garamond', 'Shippori Mincho B1', 'Noto Serif JP', serif",
  body:    "'Inter', 'Noto Sans JP', -apple-system, BlinkMacSystemFont, sans-serif",
  // Mono: 座標・時刻・コード
  mono:    "'JetBrains Mono', 'IBM Plex Mono', 'Fira Code', monospace",
};

const shadows = {
  sm:   '0 1px 2px rgba(0, 0, 0, 0.4)',
  md:   '0 4px 12px rgba(0, 0, 0, 0.5)',
  lg:   '0 8px 24px rgba(0, 0, 0, 0.6)',
  xl:   '0 16px 48px rgba(0, 0, 0, 0.7)',
  soft: '0 8px 32px rgba(0, 0, 0, 0.45)',
  glow: '0 0 30px rgba(230, 199, 126, 0.20)',
  glowSoft: '0 0 24px rgba(230, 199, 126, 0.18)',
  card: '0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 40px rgba(0,0,0,0.55)',
};

const borderRadius = {
  sm:   '6px',
  md:   '12px',
  lg:   '20px',
  xl:   '28px',
  full: '9999px',
};

const breakpoints = {
  mobile:  '480px',
  tablet:  '768px',
  desktop: '1024px',
  wide:    '1200px',
};

const containers = {
  narrow:  '720px',
  default: '1100px',
  wide:    '1440px',
};

const spacing = {
  1: '4px',  2: '8px',  3: '12px', 4: '16px',
  5: '20px', 6: '24px', 8: '32px', 10: '40px',
  12: '48px', 16: '64px', 20: '80px', 24: '96px', 32: '128px',
};

const motion = {
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  duration: {
    fast:   '180ms',
    base:   '300ms',
    slow:   '600ms',
    slowest: '1200ms',
  },
};

const theme = {
  colors,
  fonts,
  shadows,
  borderRadius,
  breakpoints,
  containers,
  spacing,
  motion,
};

export default theme;
export { colors, fonts, shadows, borderRadius, breakpoints, containers, spacing, motion };
